import { experience, featuredMetrics, products, projects, skills, workshops } from "../../../content";
import { aiCredentials, aiProjects, aiSkills } from "../AiInnovation/aiItems";
import { nonCredlyCerts, CATEGORY_ORDER } from "../../../utils/nonCredlyCerts";
import type { CertCategory, NonCredlyCert } from "../../../utils/nonCredlyCerts";
import type { ExperienceEntry, ProjectCategory, ResolvedMetric, SkillCategory } from "../../../types/content";

/**
 * ============================================================================
 * Career Dashboard — every figure the page can print, derived from the layer.
 * ============================================================================
 *
 * THE WHOLE POINT OF THIS FILE IS THAT `Dashboard.tsx` CANNOT TYPE A NUMBER.
 * A dashboard is the one page shape where a fabricated figure is invisible:
 * a chart makes a number look measured, and nobody reads a bar chart asking
 * which JSON entry each column came from. So the page renders figures ONLY
 * through `<Figure id="…">`, which looks the value up in `FIGURES` below and
 * THROWS on a miss — and the pre-render executes the component, so an
 * unregistered id fails the build rather than shipping.
 *
 * Every entry in `FIGURES` is computed from `src/content` (or, for the
 * certification counts, from the bridge that decides what the Certifications
 * section actually renders — see the note on that block). None is typed.
 *
 * THE ID IS ALSO EMITTED INTO THE MARKUP as `data-figure`, which is what makes
 * the join checkable from OUTSIDE this file. D-065's rule is that a join is
 * asserted in BOTH directions and against the BUILT HTML, not the DOM: the
 * verification sweep recomputes every value independently from the JSON — its
 * own arithmetic, its own parse of the bridge — and then requires that
 *
 *   · every `data-figure` in the built page is an id it can source, and
 *   · every id it can source is present in the built page.
 *
 * A figure this file computes and the page forgets to render is as much a
 * defect as one the page invents, and only the second direction catches it.
 *
 * WHAT IS DELIBERATELY NOT HERE. No target, no projection, no "industry
 * average", no aggregate that reads as a new claim (a "total value delivered"
 * summing unrelated metrics would be a figure this career never produced).
 * Article II.3: if the layer can settle it, derive it; if it cannot, do not
 * assert it (D-066).
 */

/** One row of a bar chart: a label, a count, and the id that carries the count. */
export interface CountBar {
	key: string;
	label: string;
	count: number;
	figureId: string;
}

/** One role on the career timeline. Geometry in months, all of it derived. */
export interface RoleBar {
	id: string;
	company: string;
	title: string;
	location: string;
	summary: string;
	tags: string[];
	/** "Dec 2020 – Dec 2024" / "Dec 2024 – Present". */
	spanLabel: string;
	/** Months from the start of the timeline to this role's start. */
	offsetMonths: number;
	/** Inclusive months in role. */
	months: number;
	current: boolean;
	spanFigureId: string;
	tenureFigureId: string;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * "2020-12" -> {year, month}. Parsed by hand, never through `Date`.
 * `new Date("2020-12")` is read as UTC and comes back as November west of
 * Greenwich — the same trap `Workshops.monthLabel` carries a note about.
 */
const parseMonth = (value: string): { year: number; month: number } => {
	const [year, month] = value.split("-").map(Number);
	return { year, month };
};

const monthLabel = (value: string): string => {
	const { year, month } = parseMonth(value);
	return `${MONTHS[month - 1]} ${year}`;
};

/** Months since the epoch month, so two dates can be subtracted. */
const absoluteMonth = (value: string): number => {
	const { year, month } = parseMonth(value);
	return year * 12 + (month - 1);
};

/**
 * "Now", as an absolute month index — READ FROM THE BUILD CONSTANT, NEVER FROM
 * THE CLOCK.
 *
 * `new Date()` here would be a hydration mismatch waiting for a month boundary:
 * the pre-render computes an open-ended tenure at build time and the browser
 * recomputes it against its own clock, so the two disagree the moment a visitor
 * loads a page built in a different month — and React does not repair a text
 * mismatch quietly, it discards the server tree and re-renders the whole
 * document (D-042). `__CONTENT_AS_OF__` is HEAD's commit date stamped in by
 * `vite.config.ts`; it is what `yearsExperience` uses and for exactly this
 * reason (see the note on it in the loader, and in declaraction.d.ts).
 *
 * The honest reading of the consequence: an open-ended tenure on this page is
 * what was true at the last deploy, not at this second. That is what a static
 * artifact can say, and it is idempotent — rebuilding the same commit twice
 * cannot produce two different numbers.
 */
const nowAbsolute = (): number => {
	const [year, month] = __CONTENT_AS_OF__.slice(0, 7).split("-").map(Number);
	if (!year || !month) {
		throw new Error(
			`dashboard figures: __CONTENT_AS_OF__ is "${__CONTENT_AS_OF__}", which is not a YYYY-MM-prefixed date. ` +
				"The open-ended tenure on the timeline cannot be derived without it."
		);
	}
	return year * 12 + (month - 1);
};

/** 49 -> "4 yr 1 mo"; 12 -> "1 yr"; 8 -> "8 mo". */
const tenureLabel = (months: number): string => {
	const years = Math.floor(months / 12);
	const rest = months % 12;
	if (years && rest) return `${years} yr ${rest} mo`;
	if (years) return `${years} yr`;
	return `${rest} mo`;
};

/* ==========================================================================
   1. THE CAREER TIMELINE — experience.json, NEWEST FIRST (owner ruling, D-097)
   ==========================================================================
   This read oldest-first until D-097, on the argument that "a timeline that
   runs backwards is a timeline nobody can read". The owner ruled the other way
   and the ruling is the one that governs — it is also the order every other
   dated section on this site already uses (`Experience_v2`, `/stories`,
   `Education_v2`), so oldest-first was the odd one out rather than a house
   convention. `experience` is itself newest-first, so this now takes the
   layer's own order and reverses nothing.

   THE ORDER IS PRESENTATION AND NOTHING ELSE DEPENDS ON IT. `offsetMonths` is
   measured from `timelineStart`, a min over every role, so the bar geometry is
   order-independent; the figure ids are per-role; `roles:total` is a length.
   Both surfaces read this one array, which is what keeps the Home strip and the
   full route from disagreeing about the order the way a second copy would.

   TWO CAPTIONS SAY THE ORDER OUT LOUD and had to move with it — Dashboard's
   panel blurb and the teaser's line under the bars. That is D-066's rule about
   labels beside computed values: "Black belt grades" and "Across karate and
   service" were both true the day they were written and false on the next edit.
   A caption stating an order is exactly that shape, and reversing an array
   without moving the words leaves the page asserting the opposite of what it
   draws.

   Tenure is INCLUSIVE of both endpoint months, which is the convention every
   resume uses: Sep 2018 – Aug 2019 is twelve months, not eleven. */
const roleEntries: ExperienceEntry[] = [...experience];

const timelineStart: number = Math.min(...roleEntries.map((role: ExperienceEntry) => absoluteMonth(role.startDate)));

export const roleBars: RoleBar[] = roleEntries.map((role: ExperienceEntry): RoleBar => {
	const start = absoluteMonth(role.startDate);
	const end = role.endDate === null ? nowAbsolute() : absoluteMonth(role.endDate);
	const months = end - start + 1;
	return {
		id: role.id,
		company: role.company,
		title: role.title,
		location: role.location,
		summary: role.summary,
		tags: role.tags,
		spanLabel: `${monthLabel(role.startDate)} – ${role.endDate === null ? "Present" : monthLabel(role.endDate)}`,
		offsetMonths: start - timelineStart,
		months,
		current: role.endDate === null,
		spanFigureId: `role:${role.id}:span`,
		tenureFigureId: `role:${role.id}:tenure`
	};
});

/** Total months the timeline spans, for the bar geometry. Never printed. */
export const timelineMonths: number = Math.max(...roleBars.map((bar: RoleBar) => bar.offsetMonths + bar.months));

/* ==========================================================================
   2. SKILLS — skills.json
   ========================================================================== */
export const skillBars: CountBar[] = skills.categories.map(
	(category: SkillCategory): CountBar => ({
		key: category.id,
		label: category.name,
		count: category.skills.length,
		figureId: `skills:${category.id}`
	})
);

const skillTotal: number = skills.categories.reduce(
	(total: number, category: SkillCategory) => total + category.skills.length,
	0
);

/* ==========================================================================
   3. CERTIFICATIONS — counted off the BRIDGE, not off certifications.json
   ==========================================================================
   AND THAT IS THE OPPOSITE OF THIS SITE'S USUAL RULE, SO IT NEEDS ITS REASON.

   `certifications.json` carries a `category` field. It is read by nothing —
   the Certifications section's filter pills come from `CardSpec.category` in
   `utils/nonCredlyCerts.ts`, which is a different taxonomy with different
   members (D-089 recorded five credentials sitting at `"category": "ai"` in the
   JSON with no effect on anything). Charting the JSON field would therefore
   publish a SECOND breakdown of the same credentials that disagrees with the
   section a reader can scroll to and count. Two published numbers for one set
   is the defect; which field is "canonical" is not the interesting question.

   So the counts are the rendered set's own, and they are the numbers the pills
   show. The FACTS behind each card — name, issuer, issue date — still come from
   `certifications.json` through that bridge, which is where they have always
   come from.

   The bridge carries 27 cards for 26 publishable credentials: `dpdpa` has no
   content-layer entry (its title, issuer and year live in the bridge), and
   `ibm-ai-healthcare` is in the layer at `pending-details` and renders nowhere.
   Both are recorded in TODO. The dashboard counts what the site publishes. */
const certCategoryCounts = new Map<CertCategory, number>();
for (const cert of nonCredlyCerts) {
	certCategoryCounts.set(cert.category, (certCategoryCounts.get(cert.category) ?? 0) + 1);
}

export const certCategoryBars: CountBar[] = CATEGORY_ORDER.filter(
	(category: CertCategory) => (certCategoryCounts.get(category) ?? 0) > 0
).map(
	(category: CertCategory): CountBar => ({
		key: category,
		label: category,
		count: certCategoryCounts.get(category) ?? 0,
		figureId: `certs:cat:${category}`
	})
);

/**
 * Credentials per year, EVERY year in the range including the empty ones.
 * A column chart that silently omits 2021 and 2022 draws a straight climb
 * where the record has a two-year gap — a shape that asserts something the
 * data does not.
 */
const certYears: number[] = nonCredlyCerts.map((cert: NonCredlyCert) => cert.year).filter((year: number) => year > 0);
const firstCertYear: number = Math.min(...certYears);
const lastCertYear: number = Math.max(...certYears);

export const certYearBars: CountBar[] = Array.from(
	{ length: lastCertYear - firstCertYear + 1 },
	(unused, index: number) => firstCertYear + index
).map(
	(year: number): CountBar => ({
		key: String(year),
		label: String(year),
		count: certYears.filter((candidate: number) => candidate === year).length,
		figureId: `certs:year:${year}`
	})
);

const accredited: number = nonCredlyCerts.filter((cert: NonCredlyCert) => cert.tier === "certification").length;
const training: number = nonCredlyCerts.filter((cert: NonCredlyCert) => cert.tier === "training").length;

/* ==========================================================================
   4. DELIVERY PORTFOLIO — products.json and projects.json
   ========================================================================== */
const PROJECT_CATEGORY_LABEL: Record<ProjectCategory, string> = {
	"healthcare-regulated": "Healthcare (regulated)",
	govtech: "GovTech",
	"enterprise-client": "Enterprise client",
	"ai-innovation": "AI innovation"
};

const projectCategoryCounts = new Map<ProjectCategory, number>();
for (const project of projects) {
	projectCategoryCounts.set(project.category, (projectCategoryCounts.get(project.category) ?? 0) + 1);
}

export const projectBars: CountBar[] = (Object.keys(PROJECT_CATEGORY_LABEL) as ProjectCategory[])
	.filter((category: ProjectCategory) => (projectCategoryCounts.get(category) ?? 0) > 0)
	.map(
		(category: ProjectCategory): CountBar => ({
			key: category,
			label: PROJECT_CATEGORY_LABEL[category],
			count: projectCategoryCounts.get(category) ?? 0,
			figureId: `projects:cat:${category}`
		})
	);

/* ==========================================================================
   THE REGISTRY
   ==========================================================================
   Assembled from the blocks above. `Dashboard.tsx` can print a number only by
   naming one of these ids, and `<Figure>` throws on a name that is not here. */
const entries: Array<[string, string]> = [
	/* The owner-curated headline figures, verbatim from the layer. `featured` is
	   an owner ruling (D-071) and this page does not second-guess it: the two
	   unfeatured metrics are published elsewhere on the site and are not
	   promoted here, because a component that re-curates the layer is the
	   failure MetricsBand's own note describes. */
	...featuredMetrics.map((metric: ResolvedMetric): [string, string] => [`metric:${metric.id}`, metric.value]),

	/* The timeline. */
	...roleBars.flatMap(
		(bar: RoleBar): Array<[string, string]> => [
			[bar.spanFigureId, bar.spanLabel],
			[bar.tenureFigureId, tenureLabel(bar.months)]
		]
	),
	["roles:total", String(roleBars.length)],

	/* Skills. */
	...skillBars.map((bar: CountBar): [string, string] => [bar.figureId, String(bar.count)]),
	["skills:total", String(skillTotal)],
	["skills:categories", String(skillBars.length)],
	["skills:top", String(skills.topSkills.length)],

	/* Certifications. */
	...certCategoryBars.map((bar: CountBar): [string, string] => [bar.figureId, String(bar.count)]),
	...certYearBars.map((bar: CountBar): [string, string] => [bar.figureId, String(bar.count)]),
	["certs:total", String(nonCredlyCerts.length)],
	["certs:tier:certification", String(accredited)],
	["certs:tier:training", String(training)],
	["certs:categories", String(certCategoryBars.length)],
	/* NO `certs:layer` HERE, AND THE ABSENCE WAS FOUND BY THE GATE (D-094).
	   The first draft registered the content layer's own verified-credential
	   count (26, against the 27 cards the section publishes) and the page
	   never rendered it. The verification sweep asserts the join in BOTH
	   directions, so it failed on the second: a figure derived and shown
	   nowhere is dead weight in a registry whose whole purpose is that it
	   lists exactly what the page prints. The 26/27 difference is site
	   plumbing — `dpdpa` is published from the bridge with no layer entry and
	   `ibm-ai-healthcare` sits in the layer at `pending-details` — and it is
	   recorded in TODO, which is where plumbing belongs. */

	/* Portfolio. */
	...projectBars.map((bar: CountBar): [string, string] => [bar.figureId, String(bar.count)]),
	["projects:total", String(projects.length)],
	["products:total", String(products.length)],
	["workshops:total", String(workshops.length)],

	/* THE AI SELECTION, COUNTED WHERE IT IS DEFINED (D-095). The Home teaser
	   for /ai-innovation prints these, and the brief that asked for the
	   teasers asked for the same guarantee the dashboard already had: a
	   teaser figure joins to a source or fails the build. Reading them off
	   `aiItems` rather than re-filtering here is the D-089 lesson — the
	   selection rule lives in one place or it drifts in several. */
	["ai:credentials", String(aiCredentials.length)],
	["ai:builds", String(aiProjects.length)],
	["ai:skills", String(aiSkills.skills.length)]
];

export const FIGURES: ReadonlyMap<string, string> = new Map(entries);

/**
 * Guard against two ids colliding in the list above — a silent overwrite that
 * would make one figure print another's value. Runs at module load, which is
 * inside the pre-render, so it fails the build.
 */
if (FIGURES.size !== entries.length) {
	throw new Error(
		`dashboard figures: ${entries.length} entries collapsed to ${FIGURES.size} — two figures share an id, ` +
			"so one of them is printing the other's value."
	);
}
